import type {
  ConversationDTO,
  ConversationDetailDTO,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { ENDPOINTS } from './endpoints';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export async function getConversations(): Promise<ConversationDTO[]> {
  const res = await apiClient.get<ConversationDTO[]>(ENDPOINTS.conversations.list);
  return res.data;
}

export async function getConversation(id: string): Promise<ConversationDetailDTO> {
  const res = await apiClient.get<ConversationDetailDTO>(ENDPOINTS.conversations.detail(id));
  return res.data;
}

export async function createConversation(initialMessage: string): Promise<ConversationDTO> {
  const res = await apiClient.post<ConversationDTO>(ENDPOINTS.conversations.create, { initialMessage });
  return res.data;
}

export async function resolveConversation(id: string): Promise<void> {
  await apiClient.post(ENDPOINTS.conversations.resolve(id));
}

export async function archiveConversation(id: string): Promise<void> {
  await apiClient.post(ENDPOINTS.conversations.archive(id));
}

/**
 * Send a message and stream the SSE response.
 * Returns a ReadableStream so the caller can parse SSE events.
 * Uses raw fetch for streaming support.
 */
export async function sendMessageStream(
  conversationId: string,
  body: {
    content: string;
    triggerAI: boolean;
    attachments?: Array<{ fileKey: string; fileName: string; fileType: string; fileUrl: string }>;
  },
): Promise<ReadableStream<Uint8Array>> {
  const token = useAuthStore.getState().accessToken;
  const url = `${API_BASE}${ENDPOINTS.conversations.messages(conversationId)}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  return response.body;
}
